/**
 * Mode Debug administrateur — analyse technique temps réel, purge sécurisée.
 *
 * Tout ce que cette page affiche pendant une session (appels API, webhooks,
 * transcriptions, réponses LLM, Copilot, WebSockets, traces d'appel) est
 * temporaire : détruit côté serveur à la désactivation ou à l'expiration.
 * Seules les actions administratives (activation, désactivation, expiration,
 * purge) restent, dans l'onglet « Activités système ».
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Bug, Activity, PhoneCall, Mic, Bot, Radio, ListTree, ShieldAlert, Trash2, Timer, Power,
  Gauge, Search, AlertTriangle, ChevronDown, ChevronRight, Building2, Sparkles, Waypoints,
} from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import { useAuth } from "@/context/CrmAuth";
import { PageHeader, KpiCard, Loader, Empty } from "@/crm/ui";
import useDebugSocket from "@/crm/components/debug/useDebugSocket";
import PurgeCompanyModal from "@/crm/components/debug/PurgeCompanyModal";
import SystemActivities from "@/crm/components/debug/SystemActivities";

const CARD = "bg-white rounded-2xl border border-slate-200";
const BTN_GHOST = "flex items-center gap-2 px-3.5 py-2 rounded-xl border border-slate-200 text-sm text-slate-600 hover:border-slate-300 disabled:opacity-40 disabled:cursor-not-allowed";
const BTN_PRIMARY = "flex items-center gap-2 px-3.5 py-2 rounded-xl bg-indigo-600 text-white text-sm font-medium hover:bg-indigo-700 disabled:opacity-60";
const FIELD = "w-full px-3 py-2.5 rounded-xl border border-slate-200 text-sm outline-none focus:border-indigo-500";
const DURATIONS = [15, 30, 60];

const TABS = [
  ["overview", "Vue d'ensemble", Gauge],
  ["telephony", "Téléphonie", PhoneCall],
  ["stt", "STT", Mic],
  ["llm", "LLM", Sparkles],
  ["copilot", "Copilot", Bot],
  ["websocket", "WebSocket", Radio],
  ["calls", "Traces d'appel", Waypoints],
  ["events", "Événements", ListTree],
  ["purge", "Purge", Trash2],
  ["audit", "Activités système", ShieldAlert],
];

const SERVICE_TONE = {
  telephony: "bg-emerald-50 text-emerald-700", stt: "bg-sky-50 text-sky-700",
  llm: "bg-violet-50 text-violet-700", copilot: "bg-indigo-50 text-indigo-700",
  websocket: "bg-amber-50 text-amber-700", http: "bg-slate-100 text-slate-600",
  debug: "bg-slate-100 text-slate-600",
};
const LEVEL_TONE = { error: "text-rose-600", warning: "text-amber-600", info: "text-slate-500" };

const hms = (iso) => (iso ? new Date(iso).toLocaleTimeString("fr-FR", { hour12: false }) : "—");
const mmss = (s) => `${String(Math.floor(Math.max(0, s) / 60)).padStart(2, "0")}:${String(Math.max(0, s) % 60).padStart(2, "0")}`;
const ms = (v) => (v == null ? "—" : `${Math.round(v)} ms`);
const n = (v) => (v == null ? "—" : Number(v).toLocaleString("fr-FR"));
const money = (v, cur = "USD") => (v == null ? "—" : `${Number(v).toFixed(4)} ${cur}`);

/** Résumé lisible d'un événement, selon son type. */
function summarize(e) {
  const m = e.metadata || {};
  switch (e.event) {
    case "PROVIDER_API_CALL": return `${m.provider} ${m.method} ${m.endpoint} → ${m.status_code ?? "?"} (${ms(m.duration_ms)})${m.error ? ` — ${m.error}` : ""}`;
    case "WEBHOOK_RECEIVED": return `${m.provider} ${m.method} ${m.path} → ${m.status_code} (${ms(m.duration_ms)})`;
    case "WEBHOOK_PAYLOAD": return `${m.provider} ${m.path}${m.call_id ? ` · ${m.call_id}` : ""}`;
    case "WEBHOOK_REJECTED": return `${m.provider} ${m.path} — ${m.reason}`;
    case "WEBHOOK_EVENT": return `${m.provider} ${m.type} ${m.status || ""} ${m.direction || ""}${m.duplicate ? " (doublon)" : ""}`;
    case "TELEPHONY_OPERATION": return `${m.provider} ${m.operation} → ${m.status} (${ms(m.duration_ms)})${m.error ? ` — ${m.error}` : ""}`;
    case "CALL_STARTED": return `${m.provider} ${m.direction || ""} ${m.phone_number || ""} ${m.call_id || ""}`;
    case "CALL_ENDED": return `${m.provider} ${m.status || ""} · ${m.duration_seconds ?? 0} s`;
    case "MEDIA_STREAM_CONNECTED": return `${m.provider} · session ${m.session_id}`;
    case "MEDIA_STREAM_CLOSED": return `${m.provider} · ${m.duration_seconds ?? 0} s`;
    case "STT_CONNECTED": return `${m.provider} ${m.model || ""} ${m.language || ""} (${ms(m.duration_ms)})`;
    case "STT_TRANSCRIPT_FINAL": return `${(m.speaker || "").toUpperCase()} : « ${m.text} »${m.confidence != null ? ` · ${Math.round(m.confidence * 100)} %` : ""}${m.latency_ms != null ? ` · ${ms(m.latency_ms)}` : ""}`;
    case "STT_DISCONNECTED": return `${m.provider} · ${m.audio_seconds ?? 0} s d'audio · ${m.segments ?? 0} segments`;
    case "STT_ERROR": case "STT_UNAVAILABLE": return `${m.provider || m.track || ""} — ${m.error}`;
    case "LLM_RESPONSE": return `${m.provider}/${m.model} ${ms(m.latency_ms)} · ${n(m.input_tokens)}/${n(m.output_tokens)} jetons${m.tokens_estimated ? " (estimés)" : ""}${m.estimated_cost != null ? ` · ${money(m.estimated_cost, m.currency)}` : ""}`;
    case "LLM_ERROR": return `${m.provider}/${m.model} — ${m.error}`;
    case "COPILOT_TRIGGER_DETECTED": return `${m.trigger} ${m.accepted ? "→ génération" : "(retenu)"}`;
    case "COPILOT_TRIGGERED": return `${m.trigger} (${m.mode})`;
    case "COPILOT_SUGGESTION": return `${m.type} · « ${m.text} »`;
    case "COPILOT_NO_SUGGESTION": return `${m.trigger} — aucune suggestion`;
    case "WS_CONNECTED": case "WS_DISCONNECTED": case "WS_ERROR": return `${m.channel}${m.user_id ? ` · ${m.user_id}` : ""}${m.error ? ` — ${m.error}` : ""}`;
    case "HTTP_ERROR": return `${m.method} ${m.path} → ${m.status_code}`;
    default: return Object.entries(m).slice(0, 4).map(([k, v]) => `${k}=${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ");
  }
}

export default function AdminDebug() {
  const { user } = useAuth();
  const [params] = useSearchParams();
  const { connected, status: wsStatus, snapshot: wsSnapshot, events: wsEvents, ended, clear, setStatus: setWsStatus } = useDebugSocket();
  const [status, setStatus] = useState(null);
  const [snapshot, setSnapshot] = useState(null);
  const [backlog, setBacklog] = useState([]);
  const [tab, setTab] = useState("overview");
  const [remaining, setRemaining] = useState(0);
  const remainingAt = useRef({ value: 0, at: Date.now() });

  // État initial et resynchronisation périodique de secours (le WebSocket
  // reste la source principale ; ce GET couvre une reconnexion lente).
  const refreshStatus = useCallback(async () => {
    try { const { data } = await api.get("/admin/debug/status"); setStatus(data); setWsStatus(data); }
    catch { /* la page reste utilisable */ }
  }, [setWsStatus]);
  const refreshSnapshot = useCallback(async () => {
    try { const { data } = await api.get("/admin/debug/snapshot"); setSnapshot(data.active ? data : null); }
    catch { /* silencieux */ }
  }, []);

  useEffect(() => { refreshStatus(); refreshSnapshot(); }, [refreshStatus, refreshSnapshot]);
  useEffect(() => { if (wsStatus) setStatus(wsStatus); }, [wsStatus]);
  useEffect(() => { if (wsSnapshot) setSnapshot(wsSnapshot); }, [wsSnapshot]);
  useEffect(() => {
    if (ended) {
      setSnapshot(null); setBacklog([]);
      toast.info(ended.reason === "expired" ? "Mode Debug expiré — données temporaires détruites"
        : "Mode Debug désactivé — données temporaires détruites");
    }
  }, [ended]);

  const active = Boolean(status?.active);
  const session = status?.session || null;

  // Rattrapage : une session déjà ouverte avant l'arrivée sur la page a des
  // événements que le WebSocket n'a pas vus.
  useEffect(() => {
    if (!active) return;
    api.get("/admin/debug/events", { params: { limit: 300 } })
      .then(({ data }) => setBacklog(data.events || [])).catch(() => {});
  }, [active, session?.id]);

  // Compte à rebours local, recalé à chaque statut serveur.
  const sessionId = session?.id;
  const sessionRemaining = session?.remaining_seconds;
  useEffect(() => {
    if (!sessionId) { setRemaining(0); return undefined; }
    remainingAt.current = { value: sessionRemaining ?? 0, at: Date.now() };
    setRemaining(sessionRemaining ?? 0);
    const t = setInterval(() => {
      const { value, at } = remainingAt.current;
      setRemaining(Math.max(0, value - Math.floor((Date.now() - at) / 1000)));
    }, 1000);
    return () => clearInterval(t);
  }, [sessionId, sessionRemaining]);

  const events = useMemo(() => {
    const seen = new Set();
    return [...backlog, ...wsEvents].filter((e) => (seen.has(e.seq) ? false : (seen.add(e.seq), true)))
      .sort((a, b) => a.seq - b.seq);
  }, [backlog, wsEvents]);

  const onEnabled = (data) => { setStatus(data); setWsStatus(data); clear(); setBacklog([]); refreshSnapshot(); };
  const disable = async () => {
    try { const { data } = await api.post("/admin/debug/disable"); setStatus(data); setWsStatus(data); setSnapshot(null); clear(); setBacklog([]); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const counters = snapshot?.counters || {};
  const urgent = active && remaining <= 60;

  return (
    <div className="max-w-[1400px]">
      <PageHeader title="Mode Debug" subtitle="Analyse technique temporaire — aucune donnée technique n'est conservée après la session."
        actions={active ? (
          <DisableButton onDisable={disable} />
        ) : null} />

      {/* Bandeau d'état */}
      <div data-testid="debug-status-bar" className={`${CARD} px-5 py-3.5 mb-5 flex flex-wrap items-center gap-4 ${active ? "border-emerald-200" : ""}`}>
        <div className="flex items-center gap-2.5">
          <span className={`h-2.5 w-2.5 rounded-full ${active ? (urgent ? "bg-amber-500 animate-pulse" : "bg-emerald-500") : "bg-slate-300"}`} />
          <span className="text-sm font-semibold text-slate-900">{active ? "ACTIF" : "INACTIF"}</span>
          {active && <span data-testid="debug-countdown" className={`font-mono text-sm ${urgent ? "text-amber-600" : "text-slate-600"}`}>{mmss(remaining)} restantes</span>}
        </div>
        {session && (
          <div className="text-xs text-slate-500 flex flex-wrap gap-x-4">
            <span>Admin : <b className="text-slate-700">{session.admin_name}</b></span>
            {session.company_name && <span>Entreprise : <b className="text-slate-700">{session.company_name}</b></span>}
            <span>Durée : {session.duration_minutes} min</span>
            <span>Début : {hms(session.started_at)}</span>
          </div>
        )}
        <div className="ml-auto flex items-center gap-2 text-xs text-slate-500">
          <span className={`h-2 w-2 rounded-full ${connected ? "bg-emerald-500" : "bg-slate-300"}`} />
          {connected ? "Flux temps réel connecté" : "Reconnexion…"}
        </div>
      </div>

      {!active && (
        <div className="grid lg:grid-cols-2 gap-5 items-start">
          <EnableCard onEnabled={onEnabled} presetCompany={params.get("company_id") ? { id: params.get("company_id"), nom: params.get("company_name") } : null} />
          <DebugPasswordCard />
        </div>
      )}

      {active && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
            <KpiCard label="API calls" value={n(counters.api_calls ?? 0)} icon={Activity} accent="indigo" testid="kpi-api-calls" />
            <KpiCard label="Téléphonie" value={n(counters.telephony ?? 0)} icon={PhoneCall} accent="emerald" testid="kpi-telephony" />
            <KpiCard label="STT" value={n(counters.stt ?? 0)} icon={Mic} accent="sky" testid="kpi-stt" />
            <KpiCard label="LLM" value={n(counters.llm ?? 0)} icon={Sparkles} accent="violet" testid="kpi-llm" />
            <KpiCard label="Erreurs" value={n(counters.errors ?? 0)} icon={AlertTriangle} accent="rose" testid="kpi-errors" />
          </div>

          <div className="flex flex-wrap gap-1.5 mb-4">
            {TABS.map(([k, label, Icon]) => {
              const on = tab === k;
              return (
                <button key={k} data-testid={`debug-tab-${k}`} onClick={() => setTab(k)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[13px] ${on ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                  <Icon size={13} /> {label}
                </button>
              );
            })}
          </div>

          {!snapshot && tab !== "purge" && tab !== "audit" && <Loader label="En attente du premier instantané…" />}
          {snapshot && tab === "overview" && <Overview snapshot={snapshot} />}
          {snapshot && tab === "telephony" && <TelephonyPanel snapshot={snapshot} events={events} />}
          {snapshot && tab === "stt" && <SttPanel snapshot={snapshot} events={events} />}
          {snapshot && tab === "llm" && <LlmPanel snapshot={snapshot} events={events} />}
          {snapshot && tab === "copilot" && <CopilotPanel snapshot={snapshot} events={events} />}
          {snapshot && tab === "websocket" && <WebSocketPanel snapshot={snapshot} />}
          {snapshot && tab === "calls" && <CallsPanel snapshot={snapshot} />}
          {snapshot && tab === "events" && <EventsPanel events={events} />}
          {tab === "purge" && <PurgePanel />}
          {tab === "audit" && <div className={`${CARD} p-5`}><SystemActivities limit={100} refreshKey={session?.id} /></div>}
        </>
      )}

      {!active && (
        <div className={`${CARD} p-5 mt-5`}>
          <h3 className="font-heading font-bold text-[15px] text-slate-900 mb-3 flex items-center gap-2"><ShieldAlert size={16} /> Activités système</h3>
          <SystemActivities limit={30} refreshKey={ended?.session_id || "idle"} />
        </div>
      )}
      {user?.impersonated_by && (
        <p className="text-xs text-amber-700 mt-4">Emprunt d'identité en cours : l'activation du Mode Debug et la purge sont refusées.</p>
      )}
    </div>
  );
}

function DisableButton({ onDisable }) {
  const [arm, setArm] = useState(false);
  useEffect(() => { if (!arm) return undefined; const t = setTimeout(() => setArm(false), 5000); return () => clearTimeout(t); }, [arm]);
  return arm ? (
    <button data-testid="debug-disable-confirm" onClick={onDisable} className="flex items-center gap-2 px-3.5 py-2 rounded-xl bg-rose-600 text-white text-sm font-medium hover:bg-rose-700">
      <Power size={15} /> Confirmer la désactivation
    </button>
  ) : (
    <button data-testid="debug-disable-btn" onClick={() => setArm(true)} className={BTN_GHOST}><Power size={15} /> Désactiver le Debug</button>
  );
}

function EnableCard({ onEnabled, presetCompany }) {
  const [duration, setDuration] = useState(30);
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const enable = async () => {
    if (!password || loading) return;
    setLoading(true);
    try {
      const body = { duration_minutes: duration, password };
      if (presetCompany?.id) body.company_id = presetCompany.id;
      const { data } = await api.post("/admin/debug/enable", body);
      toast.success(`Mode Debug activé pour ${duration} min`);
      setPassword("");
      onEnabled(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  return (
    <div className={`${CARD} p-6 max-w-[520px]`} data-testid="debug-enable-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-11 w-11 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center"><Bug size={22} /></div>
        <div>
          <h3 className="font-heading font-bold text-[17px] text-slate-900">Activer le Mode Debug</h3>
          <p className="text-xs text-slate-500">Analyse technique temporaire — 60 minutes maximum.</p>
        </div>
      </div>
      {presetCompany?.id && (
        <div className="mb-3 text-xs text-slate-600 flex items-center gap-2 rounded-xl border border-slate-200 px-3 py-2"><Building2 size={13} /> Entreprise : <b>{presetCompany.nom || presetCompany.id}</b></div>
      )}
      <div className="text-xs text-slate-500 mb-1.5">Durée</div>
      <div className="flex gap-2 mb-4">
        {DURATIONS.map((d) => (
          <button key={d} data-testid={`debug-duration-${d}`} onClick={() => setDuration(d)}
            className={`px-4 py-2 rounded-xl text-sm border ${duration === d ? "bg-slate-900 text-white border-slate-900" : "bg-white border-slate-200 text-slate-600 hover:border-slate-300"}`}>
            {d} min
          </button>
        ))}
      </div>
      {/* Un vrai <form> autour du champ mot de passe, et pas seulement pour la
          touche Entrée : Chrome range TOUS les champs sans formulaire de la page
          dans un même formulaire fictif. Ce mot de passe laissé hors <form> se
          retrouvait apparié au filtre du menu de gauche, que le gestionnaire de
          mots de passe remplissait alors avec l'email de connexion (le menu
          s'affichait vide). Le formulaire referme le périmètre ici ; le filtre du
          menu a le sien de son côté (crm/components/SidebarNav.jsx). */}
      <form onSubmit={(e) => { e.preventDefault(); enable(); }}>
        <label className="text-xs text-slate-500" htmlFor="debug-password">Mot de passe Mode Debug</label>
        <input id="debug-password" data-testid="debug-password" type="password" value={password} autoComplete="current-password"
          onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className={`${FIELD} mt-1 mb-4`} />
        <button type="submit" data-testid="debug-enable-btn" disabled={!password || loading} className={BTN_PRIMARY}>
          <Timer size={15} /> {loading ? "Activation…" : "Activer le Mode Debug"}
        </button>
      </form>
      <p className="text-[11px] text-slate-400 mt-3">L'activation est enregistrée dans les activités système. Les données techniques collectées sont détruites à la fin de la session.</p>
    </div>
  );
}

function DebugPasswordCard() {
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const ready = current && next.length >= 4 && next === confirm && !loading;
  const save = async () => {
    if (!ready) return;
    setLoading(true);
    try {
      await api.put("/admin/debug/password", { current_password: current, new_password: next });
      toast.success("Code du Mode Debug modifié");
      setCurrent(""); setNext(""); setConfirm("");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setLoading(false);
  };
  return (
    <div className={`${CARD} p-6 max-w-[520px]`} data-testid="debug-password-card">
      <div className="flex items-center gap-3 mb-4">
        <div className="h-11 w-11 rounded-xl bg-slate-100 text-slate-600 flex items-center justify-center"><ShieldAlert size={22} /></div>
        <div>
          <h3 className="font-heading font-bold text-[17px] text-slate-900">Code du Mode Debug</h3>
          <p className="text-xs text-slate-500">Distinct de votre mot de passe, stocké chiffré (haché). Exigé pour activer le Debug et purger une fiche.</p>
        </div>
      </div>
      {/* Même raison qu'au-dessus : trois champs mot de passe hors <form>
          faisaient du filtre du menu leur « identifiant » aux yeux de Chrome. */}
      <form onSubmit={(e) => { e.preventDefault(); save(); }}>
        <label className="text-xs text-slate-500" htmlFor="debug-password-current">Code actuel</label>
        <input id="debug-password-current" data-testid="debug-password-current" type="password" value={current} autoComplete="off" onChange={(e) => setCurrent(e.target.value)} placeholder="••••" className={`${FIELD} mt-1 mb-3`} />
        <label className="text-xs text-slate-500" htmlFor="debug-password-new">Nouveau code (4 caractères minimum)</label>
        <input id="debug-password-new" data-testid="debug-password-new" type="password" value={next} autoComplete="new-password" onChange={(e) => setNext(e.target.value)} placeholder="••••" className={`${FIELD} mt-1 mb-3`} />
        <label className="text-xs text-slate-500" htmlFor="debug-password-confirm">Confirmer le nouveau code</label>
        <input id="debug-password-confirm" data-testid="debug-password-confirm" type="password" value={confirm} autoComplete="new-password" onChange={(e) => setConfirm(e.target.value)} placeholder="••••"
          className={`${FIELD} mt-1 mb-4`} />
        {next && confirm && next !== confirm && <p className="text-xs text-rose-600 mb-3">Les deux saisies diffèrent.</p>}
        <button type="submit" data-testid="debug-password-save" disabled={!ready} className={BTN_GHOST}>{loading ? "Enregistrement…" : "Changer le code"}</button>
      </form>
    </div>
  );
}

// ---------- panneaux ----------
function Section({ title, icon: Icon, children, testid, right }) {
  return (
    <div className={`${CARD} p-5`} data-testid={testid}>
      <div className="flex items-center justify-between gap-3 mb-3">
        <h3 className="font-heading font-bold text-[15px] text-slate-900 flex items-center gap-2">{Icon && <Icon size={16} />} {title}</h3>
        {right}
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, tone = "text-slate-900" }) {
  return (
    <div className="min-w-0">
      <div className={`text-lg font-mono font-semibold ${tone}`}>{value}</div>
      <div className="text-[11px] text-slate-500 truncate">{label}</div>
    </div>
  );
}

function LatencyRow({ label, stats }) {
  const s = stats || {};
  return (
    <div className="grid grid-cols-[110px_repeat(5,minmax(0,1fr))] gap-2 text-xs items-center py-1.5 border-b border-slate-100 last:border-0">
      <span className="text-slate-600 font-medium">{label}</span>
      <span className="font-mono">{ms(s.avg)}</span><span className="font-mono">{ms(s.p50)}</span>
      <span className="font-mono">{ms(s.p95)}</span><span className="font-mono">{ms(s.p99)}</span>
      <span className="font-mono text-slate-400">{n(s.count)} éch.</span>
    </div>
  );
}

function Overview({ snapshot }) {
  const perf = snapshot.performance || {};
  const providers = snapshot.providers || {};
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Section title="Performance" icon={Gauge} testid="debug-performance">
        <div className="grid grid-cols-[110px_repeat(5,minmax(0,1fr))] gap-2 text-[11px] text-slate-400 uppercase tracking-wide pb-1">
          <span /><span>moy.</span><span>p50</span><span>p95</span><span>p99</span><span />
        </div>
        <LatencyRow label="Téléphonie" stats={perf.telephony} />
        <LatencyRow label="STT" stats={perf.stt} />
        <LatencyRow label="LLM" stats={perf.llm} />
        <LatencyRow label="WebSocket" stats={perf.websocket} />
        <LatencyRow label="HTTP CRM" stats={perf.http} />
      </Section>
      <Section title="Fournisseurs" icon={PhoneCall}>
        {Object.keys(providers).length === 0 ? <p className="text-xs text-slate-400">Aucun appel fournisseur pour l'instant.</p> : (
          <div className="space-y-3">
            {Object.entries(providers).map(([name, p]) => (
              <div key={name} className="rounded-xl border border-slate-100 p-3">
                <div className="text-sm font-semibold text-slate-800 mb-2">{name}</div>
                <div className="grid grid-cols-4 gap-2">
                  <Stat label="appels API" value={n(p.api_calls)} />
                  <Stat label="succès" value={n(p.success)} tone="text-emerald-600" />
                  <Stat label="erreurs" value={n(p.errors)} tone={p.errors ? "text-rose-600" : "text-slate-900"} />
                  <Stat label="latence moy." value={ms(p.latency?.avg)} />
                </div>
              </div>
            ))}
          </div>
        )}
      </Section>
      <Section title="Copilot" icon={Bot}>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <Stat label="générées" value={n(snapshot.copilot?.generated)} />
          <Stat label="affichées" value={n(snapshot.copilot?.displayed)} />
          <Stat label="utilisées" value={n(snapshot.copilot?.used)} tone="text-emerald-600" />
          <Stat label="ignorées" value={n(snapshot.copilot?.ignored)} />
          <Stat label="régénérées" value={n(snapshot.copilot?.regenerated)} />
          <Stat label="STT segments" value={n(snapshot.stt?.segments)} />
        </div>
      </Section>
      <Section title="WebSocket" icon={Radio}>
        <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
          <Stat label="connexions actives" value={n(snapshot.websocket?.active)} />
          <Stat label="envoyés" value={n(snapshot.websocket?.sent)} />
          <Stat label="reçus" value={n(snapshot.websocket?.received)} />
          <Stat label="déconnexions" value={n(snapshot.websocket?.disconnects)} />
          <Stat label="erreurs" value={n(snapshot.websocket?.errors)} tone={snapshot.websocket?.errors ? "text-rose-600" : "text-slate-900"} />
          <Stat label="latence" value={ms(snapshot.websocket?.latency?.avg)} />
        </div>
      </Section>
    </div>
  );
}

function EventTable({ rows, columns }) {
  if (!rows.length) return <p className="text-xs text-slate-400">Rien pour l'instant.</p>;
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">{columns.map((c) => <th key={c[0]} className="py-1.5 pr-3 font-medium">{c[0]}</th>)}</tr></thead>
        <tbody>
          {rows.map((e) => (
            <tr key={e.seq} className={`border-t border-slate-100 ${e.level === "error" ? "bg-rose-50/40" : ""}`}>
              {columns.map((c) => <td key={c[0]} className="py-1.5 pr-3 font-mono whitespace-nowrap max-w-[420px] truncate">{c[1](e)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TelephonyPanel({ snapshot, events }) {
  const providers = snapshot.providers || {};
  const apiRows = events.filter((e) => e.event === "PROVIDER_API_CALL").slice(-40).reverse();
  const hooks = events.filter((e) => ["WEBHOOK_RECEIVED", "WEBHOOK_EVENT", "WEBHOOK_REJECTED", "TELEPHONY_OPERATION", "CALL_STARTED", "CALL_ENDED", "MEDIA_STREAM_CONNECTED", "MEDIA_STREAM_CLOSED"].includes(e.event)).slice(-40).reverse();
  return (
    <div className="space-y-4">
      {Object.keys(providers).length === 0 && <div className={`${CARD} p-5`}><p className="text-xs text-slate-400">Aucun échange téléphonique observé pour l'instant.</p></div>}
      {Object.entries(providers).map(([name, p]) => (
        <Section key={name} title={name} icon={PhoneCall} testid={`debug-provider-${name}`}>
          <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-12 gap-2 mb-3">
            <Stat label="API calls" value={n(p.api_calls)} />
            <Stat label="2xx" value={n(p["2xx"])} tone="text-emerald-600" />
            <Stat label="4xx" value={n(p["4xx"])} tone={p["4xx"] ? "text-amber-600" : "text-slate-900"} />
            <Stat label="5xx" value={n(p["5xx"])} tone={p["5xx"] ? "text-rose-600" : "text-slate-900"} />
            <Stat label="erreurs" value={n(p.errors)} tone={p.errors ? "text-rose-600" : "text-slate-900"} />
            <Stat label="latence moy." value={ms(p.latency?.avg)} />
            <Stat label="p50" value={ms(p.latency?.p50)} />
            <Stat label="p95" value={ms(p.latency?.p95)} />
            <Stat label="p99" value={ms(p.latency?.p99)} />
            <Stat label="appels" value={n(p.calls)} />
            <Stat label="media streams" value={n(p.media_streams)} />
            <Stat label="webhooks / err." value={`${n(p.webhooks)} / ${n(p.webhooks_errors)}`} tone={p.webhooks_errors ? "text-rose-600" : "text-slate-900"} />
          </div>
        </Section>
      ))}
      <Section title="Appels API sortants" icon={Activity}>
        <EventTable rows={apiRows} columns={[
          ["heure", (e) => hms(e.ts)], ["fournisseur", (e) => e.metadata?.provider], ["méthode", (e) => e.metadata?.method],
          ["endpoint", (e) => e.metadata?.endpoint], ["statut", (e) => e.metadata?.status_code ?? "—"],
          ["durée", (e) => ms(e.metadata?.duration_ms)], ["résultat", (e) => (e.metadata?.success ? "SUCCESS" : `ERREUR ${e.metadata?.error || ""}`)],
          ["corrélation", (e) => e.correlation_id || "—"],
        ]} />
      </Section>
      <Section title="Webhooks, opérations et flux média" icon={Waypoints}>
        <EventTable rows={hooks} columns={[
          ["heure", (e) => hms(e.ts)], ["événement", (e) => e.event], ["détail", (e) => summarize(e)], ["corrélation", (e) => e.correlation_id || "—"],
        ]} />
      </Section>
    </div>
  );
}

function SttPanel({ snapshot, events }) {
  const s = snapshot.stt || {};
  const finals = events.filter((e) => e.event === "STT_TRANSCRIPT_FINAL").slice(-30).reverse();
  const errs = events.filter((e) => e.service === "stt" && e.level === "error").slice(-10).reverse();
  return (
    <div className="space-y-4">
      <Section title="Transcription (STT)" icon={Mic} testid="debug-stt">
        <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-11 gap-2">
          <Stat label="fournisseur" value={s.provider || "—"} />
          <Stat label="modèle" value={s.model || "—"} />
          <Stat label="langue" value={s.language || "—"} />
          <Stat label="audio (s)" value={n(s.audio_seconds)} />
          <Stat label="segments" value={n(s.segments)} />
          <Stat label="partiels" value={n(s.partials)} />
          <Stat label="définitifs" value={n(s.finals)} />
          <Stat label="latence moy." value={ms(s.latency?.avg)} />
          <Stat label="p95" value={ms(s.latency?.p95)} />
          <Stat label="confiance" value={s.confidence_avg != null ? `${Math.round(s.confidence_avg * 100)} %` : "—"} />
          <Stat label="erreurs" value={n(s.errors)} tone={s.errors ? "text-rose-600" : "text-slate-900"} />
        </div>
      </Section>
      <Section title="Transcriptions définitives (temporaires)" icon={ListTree}>
        {finals.length === 0 ? <p className="text-xs text-slate-400">Aucune transcription pour l'instant.</p> : (
          <ul className="space-y-1.5 text-sm">
            {finals.map((e) => (
              <li key={e.seq} className="flex gap-3"><span className="font-mono text-[11px] text-slate-400 pt-0.5">{hms(e.ts)}</span>
                <span><b className="text-xs uppercase text-slate-500 mr-1.5">{e.metadata?.speaker}</b>{e.metadata?.text}
                  {e.metadata?.confidence != null && <span className="ml-2 text-[11px] text-slate-400">{Math.round(e.metadata.confidence * 100)} %</span>}</span></li>
            ))}
          </ul>
        )}
      </Section>
      {errs.length > 0 && <Section title="Erreurs STT" icon={AlertTriangle}><EventTable rows={errs} columns={[["heure", (e) => hms(e.ts)], ["détail", (e) => summarize(e)]]} /></Section>}
    </div>
  );
}

function LlmPanel({ snapshot, events }) {
  const l = snapshot.llm || {};
  const rows = events.filter((e) => ["LLM_RESPONSE", "LLM_ERROR"].includes(e.event)).slice(-30).reverse();
  return (
    <div className="space-y-4">
      <Section title="LLM" icon={Sparkles} testid="debug-llm">
        <div className="grid grid-cols-3 sm:grid-cols-6 lg:grid-cols-10 gap-2">
          <Stat label="fournisseur" value={l.provider || "—"} />
          <Stat label="modèle" value={l.model || "—"} />
          <Stat label="requêtes" value={n(l.requests)} />
          <Stat label="succès" value={n(l.success)} tone="text-emerald-600" />
          <Stat label="erreurs" value={n(l.errors)} tone={l.errors ? "text-rose-600" : "text-slate-900"} />
          <Stat label="latence moy." value={ms(l.latency?.avg)} />
          <Stat label="p95" value={ms(l.latency?.p95)} />
          <Stat label="jetons entrée" value={n(l.input_tokens)} />
          <Stat label="jetons sortie" value={n(l.output_tokens)} />
          <Stat label="coût estimé" value={money(l.estimated_cost, l.currency)} />
        </div>
        <p className="text-[11px] text-slate-400 mt-2">
          Coût estimé à partir de la grille tarifaire configurable (Paramètres du Debug), jamais un relevé.
          {l.tokens_estimated > 0 && ` ${l.tokens_estimated} réponse(s) sans décompte fournisseur : jetons estimés.`}
          {l.cost_unknown_requests > 0 && ` ${l.cost_unknown_requests} réponse(s) sans tarif connu.`}
        </p>
      </Section>
      <PricingEditor />
      <Section title="Prompts et réponses (visibles pendant la session seulement)" icon={ListTree}>
        {rows.length === 0 ? <p className="text-xs text-slate-400">Aucun appel LLM pour l'instant.</p> : rows.map((e) => <LlmRow key={e.seq} e={e} />)}
      </Section>
    </div>
  );
}

function LlmRow({ e }) {
  const [open, setOpen] = useState(false);
  const m = e.metadata || {};
  return (
    <div className={`border-t border-slate-100 py-2 ${e.level === "error" ? "bg-rose-50/40" : ""}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left text-xs">
        {open ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        <span className="font-mono text-slate-400">{hms(e.ts)}</span>
        <span className="font-mono truncate">{summarize(e)}</span>
        {m.purpose && <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">{m.purpose}</span>}
      </button>
      {open && (
        <div className="grid md:grid-cols-2 gap-3 mt-2">
          <pre className="text-[11px] whitespace-pre-wrap bg-slate-50 rounded-lg p-2.5 max-h-72 overflow-auto">{m.prompt || "—"}</pre>
          <pre className="text-[11px] whitespace-pre-wrap bg-slate-50 rounded-lg p-2.5 max-h-72 overflow-auto">{m.response || m.error || "—"}</pre>
        </div>
      )}
    </div>
  );
}

function PricingEditor() {
  const [table, setTable] = useState(null);
  const [open, setOpen] = useState(false);
  useEffect(() => { api.get("/admin/debug/llm-pricing").then(({ data }) => setTable(data.pricing)).catch(() => {}); }, []);
  const save = async () => {
    try { const { data } = await api.put("/admin/debug/llm-pricing", { pricing: table }); setTable(data.pricing); toast.success("Grille tarifaire enregistrée"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  if (!table) return null;
  return (
    <Section title="Grille tarifaire (USD / million de jetons)" icon={Gauge} testid="debug-pricing"
      right={<button onClick={() => setOpen(!open)} className={BTN_GHOST}>{open ? "Masquer" : "Modifier"}</button>}>
      {!open ? <p className="text-xs text-slate-400">Tarifs indicatifs, à ajuster selon vos contrats. Les tarifs ne sont jamais codés dans le code métier.</p> : (
        <div className="space-y-2">
          {Object.entries(table).map(([provider, models]) => Object.entries(models).map(([model, p]) => (
            <div key={`${provider}-${model}`} className="grid grid-cols-[120px_160px_1fr_1fr] gap-2 items-center text-xs">
              <span className="font-semibold">{provider}</span><span className="font-mono text-slate-500">{model}</span>
              <input type="number" step="0.01" value={p.input} onChange={(e) => setTable({ ...table, [provider]: { ...models, [model]: { ...p, input: e.target.value } } })} className={FIELD} placeholder="entrée" />
              <input type="number" step="0.01" value={p.output} onChange={(e) => setTable({ ...table, [provider]: { ...models, [model]: { ...p, output: e.target.value } } })} className={FIELD} placeholder="sortie" />
            </div>
          )))}
          <div className="flex justify-end"><button data-testid="debug-pricing-save" onClick={save} className={BTN_PRIMARY}>Enregistrer</button></div>
        </div>
      )}
    </Section>
  );
}

function CopilotPanel({ snapshot, events }) {
  const c = snapshot.copilot || {};
  const rows = events.filter((e) => e.service === "copilot").slice(-40).reverse();
  return (
    <div className="space-y-4">
      <Section title="Live Copilot" icon={Bot} testid="debug-copilot">
        <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-2">
          <Stat label="suggestions générées" value={n(c.generated)} />
          <Stat label="affichées" value={n(c.displayed)} />
          <Stat label="utilisées" value={n(c.used)} tone="text-emerald-600" />
          <Stat label="ignorées" value={n(c.ignored)} />
          <Stat label="régénérées" value={n(c.regenerated)} />
          <Stat label="déclencheurs auto" value={n(c.auto_triggers)} />
          <Stat label="déclencheurs manuels" value={n(c.manual_triggers)} />
          <Stat label="sans suggestion" value={n(c.no_suggestion)} tone={c.no_suggestion ? "text-amber-600" : "text-slate-900"} />
        </div>
      </Section>
      <Section title="Événements Copilot" icon={ListTree}>
        <EventTable rows={rows} columns={[["heure", (e) => hms(e.ts)], ["événement", (e) => e.event], ["détail", (e) => summarize(e)], ["session", (e) => e.metadata?.session_id || "—"]]} />
      </Section>
    </div>
  );
}

function WebSocketPanel({ snapshot }) {
  const w = snapshot.websocket || {};
  const channels = w.channels || {};
  const hint = w.errors > 0 ? "Des erreurs d'envoi : vérifier le backend (exceptions) et le proxy." :
    w.disconnects > w.sent / 10 && w.disconnects > 3 ? "Beaucoup de déconnexions : plutôt le réseau / le proxy (timeouts WebSocket)." :
    w.active === 0 ? "Aucune connexion active : le frontend n'ouvre pas de canal (jeton ? URL ?)." : "Canal sain.";
  return (
    <div className="space-y-4">
      <Section title="WebSocket" icon={Radio} testid="debug-websocket">
        <div className="grid grid-cols-3 sm:grid-cols-7 gap-2 mb-3">
          <Stat label="connexions actives" value={n(w.active)} />
          <Stat label="envoyés" value={n(w.sent)} />
          <Stat label="reçus" value={n(w.received)} />
          <Stat label="déconnexions" value={n(w.disconnects)} />
          <Stat label="erreurs" value={n(w.errors)} tone={w.errors ? "text-rose-600" : "text-slate-900"} />
          <Stat label="heartbeats" value={n(w.heartbeats)} />
          <Stat label="latence envoi" value={ms(w.latency?.avg)} />
        </div>
        <p className="text-xs text-slate-500">Diagnostic : {hint}</p>
      </Section>
      <Section title="Par canal" icon={ListTree}>
        {Object.keys(channels).length === 0 ? <p className="text-xs text-slate-400">Aucun canal observé.</p> : (
          <table className="w-full text-xs">
            <thead><tr className="text-left text-[11px] uppercase tracking-wide text-slate-400">{["canal", "actives", "connexions", "déconnexions", "envoyés", "reçus", "erreurs", "heartbeats"].map((h) => <th key={h} className="py-1.5 pr-3 font-medium">{h}</th>)}</tr></thead>
            <tbody>{Object.entries(channels).map(([name, c]) => (
              <tr key={name} className="border-t border-slate-100 font-mono">
                <td className="py-1.5 pr-3 font-sans font-medium">{name}</td><td>{n(c.active)}</td><td>{n(c.connects)}</td><td>{n(c.disconnects)}</td><td>{n(c.sent)}</td><td>{n(c.received)}</td><td className={c.errors ? "text-rose-600" : ""}>{n(c.errors)}</td><td>{n(c.heartbeats)}</td>
              </tr>))}</tbody>
          </table>
        )}
      </Section>
    </div>
  );
}

function CallsPanel({ snapshot }) {
  const calls = snapshot.calls || [];
  const [selected, setSelected] = useState(null);
  const [trace, setTrace] = useState(null);
  useEffect(() => {
    if (!selected) { setTrace(null); return undefined; }
    let alive = true;
    const load = () => api.get(`/admin/debug/calls/${encodeURIComponent(selected)}`).then(({ data }) => { if (alive) setTrace(data); }).catch(() => { if (alive) setTrace(null); });
    load();
    const t = setInterval(load, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [selected]);
  return (
    <div className="grid lg:grid-cols-[360px_minmax(0,1fr)] gap-4">
      <Section title="Appels tracés" icon={Waypoints} testid="debug-calls">
        {calls.length === 0 ? <p className="text-xs text-slate-400">Aucun appel corrélé pour l'instant.</p> : (
          <ul className="space-y-1.5">
            {calls.map((c) => (
              <li key={c.correlation_id}>
                <button data-testid={`debug-call-${c.correlation_id}`} onClick={() => setSelected(c.correlation_id)}
                  className={`w-full text-left rounded-xl border px-3 py-2 text-xs ${selected === c.correlation_id ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 hover:border-slate-300"}`}>
                  <div className="font-mono font-semibold">{c.correlation_id}</div>
                  <div className={selected === c.correlation_id ? "text-slate-300" : "text-slate-500"}>
                    {hms(c.started_at)} · {c.provider || "?"} {c.direction || ""} {c.phone_number || ""} · {c.events} évén.{c.errors ? ` · ${c.errors} err.` : ""}
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </Section>
      <Section title={selected ? `Trace ${selected}` : "Trace"} icon={ListTree}>
        {!selected && <p className="text-xs text-slate-400">Sélectionnez un appel pour reconstruire son parcours CRM → fournisseur → média → STT → LLM → Copilot → WebSocket.</p>}
        {selected && !trace && <Loader label="Chargement de la trace…" />}
        {trace && (
          <ol className="relative border-l border-slate-200 ml-2 space-y-3">
            {trace.steps.map((s) => (
              <li key={s.seq} className="ml-4">
                <span className={`absolute -left-[5px] mt-1.5 h-2.5 w-2.5 rounded-full ${s.level === "error" ? "bg-rose-500" : "bg-indigo-500"}`} />
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-mono text-slate-400">{hms(s.ts)}</span>
                  <span className={`px-1.5 py-0.5 rounded ${SERVICE_TONE[s.service] || "bg-slate-100 text-slate-600"}`}>{s.service}</span>
                  <span className="font-semibold text-slate-800">{s.event}</span>
                  {(s.duration_ms ?? s.latency_ms) != null && <span className="font-mono text-slate-500">{ms(s.duration_ms ?? s.latency_ms)}</span>}
                  {s.status_code != null && <span className="font-mono text-slate-500">HTTP {s.status_code}</span>}
                </div>
                {(s.text || s.endpoint || s.error || s.operation || s.type) && (
                  <div className={`text-xs mt-0.5 ${s.error ? "text-rose-600" : "text-slate-600"}`}>{s.error || s.text || s.endpoint || s.operation || s.type}</div>
                )}
              </li>
            ))}
          </ol>
        )}
      </Section>
    </div>
  );
}

function EventsPanel({ events }) {
  const [service, setService] = useState("");
  const [level, setLevel] = useState("");
  const [q, setQ] = useState("");
  const rows = useMemo(() => events.filter((e) => (!service || e.service === service) && (!level || e.level === level)
    && (!q || JSON.stringify(e).toLowerCase().includes(q.toLowerCase()))).slice(-300).reverse(), [events, service, level, q]);
  return (
    <Section title="Flux d'événements (temps réel)" icon={ListTree} testid="debug-events"
      right={<div className="flex gap-2">
        <select data-testid="debug-events-service" value={service} onChange={(e) => setService(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs">
          <option value="">Tous les services</option>{["telephony", "stt", "llm", "copilot", "websocket", "http", "debug"].map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select data-testid="debug-events-level" value={level} onChange={(e) => setLevel(e.target.value)} className="px-2 py-1.5 rounded-lg border border-slate-200 text-xs">
          <option value="">Tous niveaux</option><option value="error">Erreurs</option><option value="warning">Avertissements</option><option value="info">Info</option>
        </select>
        <div className="relative"><Search size={13} className="absolute left-2 top-2 text-slate-400" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filtrer…" className="pl-7 pr-2 py-1.5 rounded-lg border border-slate-200 text-xs w-40" /></div>
      </div>}>
      {rows.length === 0 ? <Empty label="Aucun événement" icon={ListTree} /> : (
        <div className="max-h-[640px] overflow-auto">{rows.map((e) => <EventRow key={e.seq} e={e} />)}</div>
      )}
    </Section>
  );
}

function EventRow({ e }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-slate-100 py-1.5">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left text-xs">
        {open ? <ChevronDown size={13} className="shrink-0" /> : <ChevronRight size={13} className="shrink-0" />}
        <span className="font-mono text-slate-400 shrink-0">{hms(e.ts)}</span>
        <span className={`px-1.5 py-0.5 rounded shrink-0 ${SERVICE_TONE[e.service] || "bg-slate-100 text-slate-600"}`}>{e.service}</span>
        <span className={`font-semibold shrink-0 ${LEVEL_TONE[e.level] || "text-slate-700"}`}>{e.event}</span>
        <span className="font-mono text-slate-600 truncate">{summarize(e)}</span>
        {e.correlation_id && <span className="ml-auto font-mono text-[10px] text-slate-400 shrink-0">{e.correlation_id}</span>}
      </button>
      {open && <pre className="text-[11px] whitespace-pre-wrap bg-slate-50 rounded-lg p-2.5 mt-1.5 max-h-72 overflow-auto">{JSON.stringify(e.metadata, null, 2)}</pre>}
    </div>
  );
}

function PurgePanel() {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [target, setTarget] = useState(null);
  const [refresh, setRefresh] = useState(0);
  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      api.get("/companies", { params: { search: q.trim(), limit: 8 } })
        .then(({ data }) => { if (alive) setResults(data.items || []); }).catch(() => {});
    }, 250);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);
  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <Section title="Purger une fiche entreprise" icon={Trash2} testid="debug-purge">
        <p className="text-xs text-slate-500 mb-3">Supprime le suivi commercial d'une fiche (activités, contacts, notes, tâches, emails, appels, sessions Copilot, contenus IA) et la remet à l'état « Nouveau ». Le référentiel et les oppositions sont conservés. Mot de passe Mode Debug requis.</p>
        <div className="relative mb-3"><Search size={14} className="absolute left-3 top-3 text-slate-400" />
          <input data-testid="purge-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher une entreprise (nom, SIRET…)" className={`${FIELD} pl-9`} /></div>
        <ul className="space-y-1.5">
          {results.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 rounded-xl border border-slate-200 px-3 py-2">
              <div className="min-w-0"><div className="text-sm font-medium text-slate-800 truncate">{c.nom}</div><div className="text-[11px] text-slate-500">{c.siret || "—"} · {c.ville || "—"} · {c.status}</div></div>
              <button data-testid={`purge-pick-${c.id}`} onClick={() => setTarget(c)} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-rose-200 text-xs text-rose-600 hover:bg-rose-50"><Trash2 size={13} /> Purger</button>
            </li>
          ))}
          {q.trim().length >= 2 && results.length === 0 && <li className="text-xs text-slate-400">Aucune entreprise trouvée.</li>}
        </ul>
        {target && <PurgeCompanyModal company={target} onClose={() => setTarget(null)} onDone={() => { setTarget(null); setRefresh((r) => r + 1); }} />}
      </Section>
      <Section title="Purges récentes" icon={ShieldAlert}><SystemActivities limit={20} refreshKey={refresh} /></Section>
    </div>
  );
}
